import * as React from 'react';

import { CannedCollectionModule } from '@coursera-web/cds-labs';

import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';

export const useShouldShowCampaignCannedCollectionModule = () => {
  const { campaignData, loading } = useCampaignModulesData();
  return !!campaignData?.cannedCollectionModule?.contentDataJson || loading;
};

const CampaignCannedCollectionModuleRenderer = () => {
  const track = useTracker();

  const moduleData = useCampaignModulesData()?.campaignData?.cannedCollectionModule;
  const content = moduleData?.contentDataJson;

  const {
    backgroundColor,
    cardsCollection,
    color,
    heading,
    showAllEntries,
    size,
    subheading,
    stickToPrevious,
    showImageInFull,
  } = content || {};

  const moduleEventingData = {
    moduleType: 'canned_collection_module' satisfies MerchandisingModuleModuleType,
    heading: heading ?? '',
    body: subheading ?? '',
    campaign: {
      id: moduleData?.campaignId ?? '',
      matchedCountryCode: moduleData?.matchedCountryCode ?? '',
      epicExperimentId: moduleData?.epicExperimentId ?? '',
      regionName: moduleData?.regionName ?? '',
      name: moduleData?.campaignName ?? '',
    },
  } as const;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: moduleEventingData,
    }
  );

  if (!useShouldShowCampaignCannedCollectionModule()) {
    return null;
  }

  const cards = cardsCollection?.items ?? [];
  const filteredCards = cards.filter(Boolean) as Array<NonNullable<typeof cards>[number]>;

  const itemCards = filteredCards
    .map((card) => {
      const onClick = () => {
        track('click_merchandising_module', {
          merchandisingModule: moduleEventingData,
          button: { name: 'primary_cta', linkURL: card?.target ?? '' },
        });
      };

      return {
        ...card,
        id: card.sys.id,
        onClick,
      };
    })
    .filter((v) => !!v);

  return (
    <CannedCollectionModule
      cards={itemCards}
      backgroundColor={backgroundColor}
      color={color}
      heading={heading}
      subheading={subheading}
      size={size}
      showAllEntries={showAllEntries}
      removeTopPadding={stickToPrevious}
      ref={visibilityTrackingRef}
      showImageInFull={showImageInFull}
    />
  );
};

export default CampaignCannedCollectionModuleRenderer;
